import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import tabMenuList from "@plannel/pages/TabMenuList";
import menuAccessUtil from "@plannel/pages/utils/menu-access-util";
import userService from "@plannel/services/system/user-service";
import debug from "@plannel/utils/debug-util";

/**
 * 화면 안에서 <b>다른 화면으로 거는 링크</b>의 선택지.
 *
 * <p>워크플로우 단계와 회의 안건이 같은 것을 고른다 — 고를 수 있는 화면과 그 판정 규칙은
 * <b>한 벌이어야 한다.</b> 각자 들고 있으면 한쪽에만 화면이 늘거나 역할 규칙이 바뀌어,
 * 같은 사용자가 화면에 따라 다른 목록을 보게 된다.
 *
 * <p><b>권한 없는 화면은 선택지에 아예 넣지 않는다.</b> 사이드바 노출 규칙(lv1 sideMenu +
 * 각 레벨 roleCheck)과 같은 판정을 쓴다 — 고르게 해 놓고 눌렀을 때 막는 것은 고른 사람이
 * 무엇을 잘못했는지 알 수 없게 만든다.
 *
 * @returns {{options: Array, groupIcons: Map, pageMap: Map, ready: boolean}}
 *   options 는 {key, label, group, icon}, pageMap 은 key 로 찾는 같은 값이다.
 *   ready 는 권한을 받아 왔는지 — 받기 전에는 목록이 비어 있어 "권한 없음" 과 구분되지 않는다.
 */

/** 링크 대상에서 뺄 lv1 메뉴 — 설정 화면은 업무 절차나 회의 안건의 링크 대상이 아니다 */
const EXCLUDED_LV1_MENUS = ["USER_ACCOUNT", "SETTING"];

/*
 * Data Integration 은 기본 제외하되, DI 역할(ROLE_DI)과 관리자(ROLE_ADMIN) 에게는 그 하위
 * 'Data Management'(DATA_MGMT) 그룹만 링크 대상으로 허용한다(CSV Import / Build Data Cube
 * 등 나머지 DI 화면은 계속 제외). ROLE_ADMIN 은 TabMenuList 의 lv1·lv2 정의에서도 이미 허용
 * 대상이라, 여기서만 막으면 사이드바에서는 보이는 화면이 링크로는 못 고르는 상태가 된다.
 */
const DATA_MGMT_ROLES = ["ROLE_DI", "ROLE_ADMIN"];
const DATA_INTEGRATION_CD = "DATA_INTEGRATION";
const DATA_MGMT_CD = "DATA_MGMT";

/** 그 화면을 탭으로 연다. App 이 듣고 데려간다 */
export const openMenuTab = (menuKey) => {
  window.dispatchEvent(new CustomEvent("plannel:openMenu", { detail: { key: menuKey } }));
};

export const usePageLinkOptions = () => {
  const { t, i18n } = useTranslation();
  const [userAccess, setUserAccess] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    const username = stored ? JSON.parse(stored)?.username : null;
    if (!username) {
      return;
    }
    userService
      .getUserAccessRoles({ username })
      .then((response) => setUserAccess(response.data))
      .catch((e) => debug(e));
  }, []);

  const { options, groupIcons } = useMemo(() => {
    if (!userAccess) {
      return { options: [], groupIcons: new Map() };
    }
    const found = [];
    const groupIconMap = new Map();
    const pushLeaf = (leaf, groupLabel) => {
      if (!leaf?.key || leaf.hide) return;
      if (!menuAccessUtil.roleCheck(userAccess, leaf.appRoles, leaf.userRoles, leaf.reduxKey)) return;
      const label = leaf.skipI18n ? leaf.title : t(leaf.title);
      found.push({ key: leaf.key, label, group: groupLabel, icon: leaf.icon ?? null });
    };

    const hasDataMgmtRole = !!userAccess.roles?.some((role) => DATA_MGMT_ROLES.includes(role));

    tabMenuList.makeSideMenuList().forEach((lv1) => {
      if (EXCLUDED_LV1_MENUS.includes(lv1.lv1MenuCd) || EXCLUDED_LV1_MENUS.includes(lv1.reduxKey)) return;
      const isDataIntegration = lv1.lv1MenuCd === DATA_INTEGRATION_CD || lv1.reduxKey === DATA_INTEGRATION_CD;
      if (isDataIntegration && !hasDataMgmtRole) return;
      if (!lv1.sideMenu || !menuAccessUtil.roleCheck(userAccess, lv1.appRoles, lv1.userRoles, lv1.reduxKey)) return;
      const lv1Label = t(lv1.title);
      groupIconMap.set(lv1Label, lv1.icon ?? null);
      lv1.lv2Menu?.forEach((item, idx) => {
        if (isDataIntegration && item.menuCd !== DATA_MGMT_CD) return;
        if (lv1.subMenu[idx]) {
          if (!menuAccessUtil.roleCheck(userAccess, item.appRoles, item.userRoles, item.reduxKey)) return;
          item.lv3Menu?.forEach((leaf) => pushLeaf(leaf, lv1Label));
        } else {
          pushLeaf(item, lv1Label);
        }
      });
    });
    return { options: found, groupIcons: groupIconMap };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAccess, t, i18n.language]);

  const pageMap = useMemo(() => {
    const map = new Map();
    options.forEach((option) => map.set(option.key, option));
    return map;
  }, [options]);

  return { options, groupIcons, pageMap, ready: !!userAccess };
};

export default usePageLinkOptions;
